import React, { useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import SendRounded from "@mui/icons-material/SendRounded";
import AgentChatMessageBubble from "../common/AgentChatMessageBubble";
import type { JourneyChatMessage } from "../../contexts/ActiveJourneyContext";

const CONFIRMATION_PROMPTS = [
  "Yes, that's right — build my journey",
  "Not quite — let me clarify",
] as const;

export const CONCIERGE_CHAT_HEIGHT = {
  xs: "min(52dvh, 420px)",
  md: "min(calc(100dvh - 11rem), 580px)",
} as const;

interface ConciergeConversationPanelProps {
  messages: JourneyChatMessage[];
  loading: boolean;
  awaitingConfirmation: boolean;
  sessionReady: boolean;
  onSend: (text: string) => void;
  onStartOver: () => void;
}

const ConciergeConversationPanel: React.FC<ConciergeConversationPanelProps> = ({
  messages,
  loading,
  awaitingConfirmation,
  sessionReady,
  onSend,
  onStartOver,
}) => {
  const theme = useTheme();
  const endRef = useRef<HTMLDivElement>(null);
  const [reply, setReply] = React.useState("");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, awaitingConfirmation]);

  const handleReplySubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = reply.trim();
    if (!trimmed || loading || !sessionReady) return;
    onSend(trimmed);
    setReply("");
  };

  const accentGradient =
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #4285f4 0%, #34a853 50%, #fbbc04 100%)"
      : "linear-gradient(135deg, #4285f4 0%, #34a853 45%, #ea4335 85%)";

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: CONCIERGE_CHAT_HEIGHT,
        maxHeight: CONCIERGE_CHAT_HEIGHT,
        borderRadius: 3,
        overflow: "hidden",
        border: 1,
        borderColor: "divider",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(0,0,0,0.35)"
            : "0 8px 28px rgba(60,64,67,0.12)",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
          background: (t) =>
            t.palette.mode === "dark"
              ? alpha(t.palette.primary.main, 0.08)
              : alpha(t.palette.primary.main, 0.04),
        }}
      >
        <Avatar
          sx={{
            width: 36,
            height: 36,
            background: accentGradient,
            color: "#fff",
          }}
        >
          <AutoAwesomeOutlined sx={{ fontSize: 20 }} />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} lineHeight={1.2}>
            SAMi Concierge
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? "Thinking…"
              : awaitingConfirmation
                ? "Confirm your intent to continue"
                : "Guiding your integration journey"}
          </Typography>
        </Box>
        <Tooltip title="Start a new conversation">
          <IconButton
            size="small"
            onClick={onStartOver}
            aria-label="Start over"
            sx={{ color: "text.secondary" }}
          >
            <RefreshOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          px: { xs: 1.5, sm: 2 },
          py: 2,
          WebkitOverflowScrolling: "touch",
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? alpha(t.palette.common.white, 0.02)
              : alpha(t.palette.grey[100], 0.6),
        }}
      >
        {messages.length === 0 && !loading ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 6, px: 2 }}
          >
            Your conversation will appear here.
          </Typography>
        ) : (
          <Stack spacing={2.5}>
            {messages.map((message) => {
              const isUser = message.role === "user";
              return (
                <Box
                  key={message.id}
                  sx={{
                    display: "flex",
                    flexDirection: isUser ? "row-reverse" : "row",
                    alignItems: "flex-start",
                    gap: 1.25,
                  }}
                >
                  {!isUser && (
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        mt: 0.25,
                        background: accentGradient,
                        color: "#fff",
                      }}
                    >
                      <AutoAwesomeOutlined sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      maxWidth: { xs: "92%", sm: isUser ? "72%" : "82%" },
                      minWidth: 0,
                      "& .MuiPaper-root": {
                        maxWidth: "100%",
                        borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                        boxShadow: "none",
                      },
                    }}
                  >
                    <AgentChatMessageBubble
                      role={message.role}
                      content={message.content}
                      structuredBlockPreset={
                        message.role === "assistant" ? "concierge" : undefined
                      }
                    />
                  </Box>
                </Box>
              );
            })}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    background: accentGradient,
                    color: "#fff",
                  }}
                >
                  <AutoAwesomeOutlined sx={{ fontSize: 16 }} />
                </Avatar>
                <AgentChatMessageBubble
                  role="assistant"
                  content=""
                  isStreaming
                  structuredBlockPreset="concierge"
                />
              </Box>
            )}
          </Stack>
        )}
        <div ref={endRef} />
      </Box>

      {awaitingConfirmation && (
        <Box
          component="form"
          onSubmit={handleReplySubmit}
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.25 }}>
            {CONFIRMATION_PROMPTS.map((prompt) => (
              <Chip
                key={prompt}
                label={prompt}
                size="small"
                clickable
                disabled={loading || !sessionReady}
                variant="outlined"
                onClick={() => onSend(prompt)}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              minRows={1}
              size="small"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Reply to confirm or refine your goal…"
              disabled={!sessionReady || loading}
              InputProps={{
                sx: {
                  borderRadius: 2.5,
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? alpha(t.palette.common.white, 0.04)
                      : "grey.50",
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!sessionReady || loading || !reply.trim()}
              sx={{
                minWidth: 44,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                px: 1.5,
              }}
              aria-label="Send reply"
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendRounded />
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ConciergeConversationPanel;
